import { IModuleCardProps } from "./ModuleCard.interface";
import Placeholder from "./../../shared/assets/imgs/placeholder.svg";
import { Link } from "react-router-dom";
import { routes } from "../../shared/router/routes";
import { useCallback, useContext, useMemo } from "react";
import { TranslationContext } from "../../shared/providers/translation/translation.provider";
import FavoriteButton from "../FavoriteButton/FavoriteButton";
import { useSetModuleFavorite, useSetModuleUnfavorite } from "../../shared/hooks/useModules";
import { LoadingContext } from "../../shared/providers/loading/loading.provider";
import { ModuleContext } from "../../shared/providers/module/module.provider";
import { getFromattedPath } from "../../shared/helpers/utils";
import { CONTENT_BASE_URL } from "../../shared/constants";
import { UserContext } from "../../shared/providers/user/user.provider";

function ModuleCard(module: IModuleCardProps) {
    const { isSmall } = module;
    const { translate } = useContext(TranslationContext);
    const { setIsLoading } = useContext(LoadingContext);
    const { setUpdated } = useContext(ModuleContext);
    const { user } = useContext(UserContext);
    
    const setFavorite = useSetModuleFavorite();
    const setUnfavorite = useSetModuleUnfavorite();

    const handleFavorite = async () => {
        setIsLoading(true);

        if (!module.isFavorite) await setFavorite.mutateAsync(module.id);
        else await setUnfavorite.mutateAsync(module.id);

        setUpdated(new Date());
        setIsLoading(false);
    };

    const unitsCompleted = useMemo(() => {
        return (
            module.unitsProgress?.filter(
                (unit) =>
                    unit.completedItems ===
                    module.units?.find((u) => u.id === unit.id)?.items.length
            ).length ?? 0
        );
    }, [module]);

    return (
        <>
            {!isSmall && (
                <article className="card card_module d-flex flex-column gap-1 w-100">
                    <Link to={getFromattedPath(routes.MODULE.path, { moduleId: module.id })}>
                        <figure className="card_thumbnail">
                            <img
                                src={
                                    module.image?.url
                                        ? `${CONTENT_BASE_URL}${module.image?.url}`
                                        : Placeholder
                                }
                                alt={module.image?.alt ?? module.title}
                            />
                        </figure>
                    </Link>
                    <div className="card_info px-1 d-flex flex-column gap-1 h-100">
                        {(!!module.tag || module.moduleProgress === 100) && (
                            <div className="d-flex justify-content-between">
                                <span
                                    className={`color-${module?.tag_color ?? "yellow"}-500 text-bd4-bd text-uppercase`}>
                                    {module.tag}
                                </span>
                                {module.moduleProgress === 100 && (
                                    <i className="icon icon-24 icon-Checkmark--filled color-green-500" />
                                )}
                            </div>
                        )}
                        <Link
                            to={getFromattedPath(routes.MODULE.path, { moduleId: module.id })}
                            className="d-flex flex-column gap-1"
                        >
                            <h3 className="text-h5-bd color-neutrals-black lineclamp-2">{module.title}</h3>

                            {!!module.moduleProgress && module.moduleProgress < 100 && (
                                <div className="my-01 d-flex gap-02 align-items-center">
                                    <progress
                                        className="card_progress"
                                        value={(module.moduleProgress ?? 0)}
                                        max="100"></progress>
                                    <span className="text-bd4-md color-neutrals-neutral">
                                        {(module.moduleProgress ?? 0)}%
                                    </span>
                                </div>
                            )}
                        </Link>
                        <div className="d-flex justify-content-between gap-1 pt-2 pb-1 mt-auto">
                            {!!module.contentAmount && (
                                <span className="text-bd2-rg color-neutrals-neutral">
                                    {`${module.contentAmount} ${
                                        module.contentAmount > 1
                                            ? translate("common.units", "units")
                                            : translate("common.unit", "unit")
                                    }`}
                                </span>
                            )}
                            <FavoriteButton
                                className="ms-auto"
                                isFavorite={module?.isFavorite}
                                onClick={handleFavorite}
                            />
                        </div>
                    </div>
                </article>
            )}
            {isSmall && (
                <Link
                    className="card card_unit is_module d-flex gap-1 p-02 pe-1"
                    to={`${getFromattedPath(routes.MODULE.path, { moduleId: module.id })}`}>
                    <figure className="unit_thumbnail">
                        <img
                            src={
                                module.image?.url
                                    ? `${CONTENT_BASE_URL}${module.image?.url}`
                                    : Placeholder
                            }
                            alt={module.image?.alt ?? module.title}
                        />
                    </figure>
                    <div className="w-100 d-flex flex-column gap-02">
                        <div className="d-flex justify-content-between">
                            <span
                                className={`color-${module?.tag_color ?? "yellow"}-500 text-bd4-bd text-uppercase`}>
                                {module.tag}
                            </span>
                            {module.moduleProgress === 100 && (
                                <i className="icon icon-24 icon-Checkmark--filled color-green-500" />
                            )}
                        </div>
                        <h2 className="lineclamp-1 text-bd2-bd color-neutrals-black">
                            {module.title}
                        </h2>
                        <p className="lineclamp-1 text-bd2-md color-neutrals-dark my-0">
                            {module.description}
                        </p>
                        {!!module.unitsProgress?.length && (
                            <div className="my-01 d-flex gap-02 align-items-center">
                                <progress
                                    className="card_progress"
                                    value={(unitsCompleted / (module.units?.length ?? 0)) * 100}
                                    max="100"></progress>
                                <span className="text-bd4-md color-neutrals-neutral">
                                    {unitsCompleted}/{module.units?.length}
                                </span>
                            </div>
                        )}
                    </div>
                </Link>
            )}
        </>
    );
}

export default ModuleCard;
